import { ParsedUrlQuery } from "querystring";
import {
  isPackagingDocumentKind,
  parseCartDisplayMode,
  PackagingPriceDisplayMode,
} from "./packaging-document-kind";
import {
  PackagingBuilderDocumentItemViewModel,
  PackagingBuilderPackageState,
  PackagingHierarchyBuilderState,
  PackagingMaterialSubtypeOption,
  PackagingMaterialTypeOption,
  PackagingPackageMatchRequestNode,
  PackagingDimensionViewModel,
  PackagingHierarchyItemViewModel,
  PackagingHierarchyNodeViewModel,
  PackagingHierarchyViewModel,
  PackagingEntityType,
  PackagingItemViewModel,
  PackagingPackageSourceType,
  PackagingSourceContext,
} from "./types";

/** UI materialTypeKey for Cart item packages (always lowercase — matches normalizeMaterialTypeKey). */
const CART_ITEM_PACKAGE_TYPE_KEY = "documentitem";
/** Material Service materialTypeKey for carton packages (MaterialsEnum.Carton, lowercased). */
const CARTON_PACKAGE_TYPE_KEY = "carton";
const PACKAGING_DOCUMENT_ITEM_CODE = "PACK";
const PREDEFINED_PACKAGE_SOURCE_TYPE: PackagingPackageSourceType = "predefined";
const DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE: PackagingPackageSourceType =
  "documentItem";

const isCartItemMaterialTypeKey = (value: string | null | undefined): boolean =>
  normalizeMaterialTypeKey(value) === CART_ITEM_PACKAGE_TYPE_KEY;

const isCartonMaterialTypeKey = (value: string | null | undefined): boolean =>
  normalizeMaterialTypeKey(value) === CARTON_PACKAGE_TYPE_KEY;

const getSingleQueryValue = (
  value: string | string[] | undefined,
): string | undefined => (Array.isArray(value) ? value[0] : value);

const resolvePackagingSourceContext = (
  query: ParsedUrlQuery,
): PackagingSourceContext => {
  const sourceDocumentType = getSingleQueryValue(query.sourceDocumentType) as
    | PackagingEntityType
    | undefined;
  const sourceDocumentId = getSingleQueryValue(query.sourceDocumentId);
  const sourceDocumentNumber = getSingleQueryValue(query.sourceDocumentNumber);
  const orderId = getSingleQueryValue(query.orderId);
  const quoteId = getSingleQueryValue(query.quoteId);

  if (
    isPackagingDocumentKind(sourceDocumentType) &&
    sourceDocumentId
  ) {
    return {
      entityType: sourceDocumentType,
      entityId: sourceDocumentId,
      orderId,
      quoteId,
      sourceDocumentType,
      sourceDocumentId,
      sourceDocumentNumber,
    };
  }

  if (quoteId) {
    return {
      entityType: "quote",
      entityId: quoteId,
      orderId,
      quoteId,
      sourceDocumentType: "quote",
      sourceDocumentId: quoteId,
      sourceDocumentNumber,
    };
  }

  if (orderId) {
    return {
      entityType: "order",
      entityId: orderId,
      orderId,
      quoteId,
      sourceDocumentType: "order",
      sourceDocumentId: orderId,
      sourceDocumentNumber,
    };
  }

  return {
    entityType: null,
    entityId: null,
    orderId,
    quoteId,
    sourceDocumentType,
    sourceDocumentId,
    sourceDocumentNumber,
  };
};

const parseNumericValue = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value.replace(/,/g, "."));
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return 0;
};

const firstNonEmptyValue = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string" && value.trim() !== "") {
      return value.trim();
    }
  }

  return undefined;
};

const normalizeMaterialTypeKey = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim().toLowerCase();
  return normalizedValue || null;
};

const isTruthyShowInPackagingItemsFlag = (value: unknown): boolean =>
  value === true || value === "true" || value === 1 || value === "1";

/**
 * Document items include ShowInPackagingItems from get-document.
 * Only items with the flag set are listed in the packaging items panel
 * (covers former PACK code / packaging product-type exclusions).
 */
const shouldShowDocumentItemInPackagingItems = (item: any): boolean =>
  isTruthyShowInPackagingItemsFlag(
    item?.showInPackagingItems ?? item?.ShowInPackagingItems,
  );

const getDocumentItemProductCode = (item: any): string =>
  (
    firstNonEmptyValue(
      item?.productCode,
      item?.code,
      item?.Code,
      item?.sku,
      item?.itemCode,
      item?.catalogNumber,
    ) ?? ""
  )
    .trim()
    .toUpperCase();

const isPackagingDocumentItemCode = (item: any): boolean =>
  getDocumentItemProductCode(item) === PACKAGING_DOCUMENT_ITEM_CODE;

const documentHasPackagingItem = (documentItems: any[] | undefined): boolean =>
  Array.isArray(documentItems) &&
  documentItems.some((item) => isPackagingDocumentItemCode(item));

/**
 * Warn on delete when removing a packaging (PACK) line, or a packaging-list
 * item while a PACK line exists on the document.
 */
const shouldWarnPackagingOnItemDelete = (
  item: any,
  documentItems: any[] | undefined,
): boolean => {
  if (!item) {
    return false;
  }
  if (isPackagingDocumentItemCode(item)) {
    return true;
  }
  return (
    shouldShowDocumentItemInPackagingItems(item) &&
    documentHasPackagingItem(documentItems)
  );
};

const isRecord = (value: unknown): value is Record<string, any> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const resolvePackageSourceType = (
  value: unknown,
): PackagingPackageSourceType => {
  const normalized = firstNonEmptyValue(value)?.toLowerCase();
  if (normalized === DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE) {
    return DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE;
  }
  return PREDEFINED_PACKAGE_SOURCE_TYPE;
};

const isDocumentItemPackage = (
  packageState: Pick<
    PackagingBuilderPackageState,
    | "packageSourceType"
    | "sourceDocumentItemId"
    | "materialTypeKey"
  > | null | undefined,
): boolean =>
  Boolean(
    packageState &&
      (packageState.packageSourceType === DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE ||
        isCartItemMaterialTypeKey(packageState.materialTypeKey)) &&
      packageState.sourceDocumentItemId,
  );

const formatDocumentItemSubtypeLabel = (
  documentItem: Pick<PackagingItemViewModel, "name" | "code" | "jobName">,
): string => {
  const name = documentItem.name?.trim();
  const jobName = documentItem.jobName?.trim();
  const code = documentItem.code?.trim();

  if (name && jobName) {
    return `${name} - ${jobName}`;
  }
  if (name && code) {
    return `${name} - ${code}`;
  }
  return name || jobName || code || "";
};

const parseNullableNumericValue = (value: unknown): number | null => {
  if (value == null || value === "") {
    return null;
  }

  const parsed = parseNumericValue(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const mapDimensionValue = (
  value: unknown,
): PackagingDimensionViewModel | null => {
  if (!isRecord(value)) {
    return null;
  }

  const width = parseNullableNumericValue(
    value.width ??
      value.Width ??
      value.w ??
      value.W,
  );
  const height = parseNullableNumericValue(
    value.height ??
      value.Height ??
      value.h ??
      value.H,
  );
  const length = parseNullableNumericValue(
    value.length ??
      value.Length ??
      value.l ??
      value.L,
  );

  if (width === null && height === null && length === null) {
    return null;
  }

  return {
    width,
    height,
    length,
  };
};

const getParameterValueByCodes = (
  parameters: unknown,
  codes: string[],
): number | null => {
  if (!Array.isArray(parameters)) {
    return null;
  }

  const normalizedCodes = new Set(
    codes.map((code) => code.trim().toLowerCase()).filter(Boolean),
  );

  for (const parameter of parameters) {
    if (!isRecord(parameter)) {
      continue;
    }

    const parameterCode = firstNonEmptyValue(
      parameter.parameterCode,
      parameter.ParameterCode,
      parameter.code,
      parameter.Code,
      parameter.name,
      parameter.Name,
      parameter.valueId,
      parameter.ValueId,
    )?.toLowerCase();

    if (!parameterCode || !normalizedCodes.has(parameterCode)) {
      continue;
    }

    const rawValue =
      parameter.values?.[0] ??
      parameter.Values?.[0] ??
      parameter.value ??
      parameter.Value;

    const nestedValue =
      isRecord(rawValue) && "value" in rawValue
        ? (rawValue as { value: unknown }).value
        : rawValue;

    const parsed = parseNullableNumericValue(nestedValue);
    if (parsed !== null) {
      return parsed;
    }
  }

  return null;
};

/**
 * Resolve item dimensions from Quote/Order document item payloads.
 * Document items usually do not expose a top-level `unitSize`; sizes often live in
 * finalUnit* / unit* fields or product parameters.
 */
const extractDocumentItemUnitSize = (
  item: unknown,
): PackagingDimensionViewModel | null => {
  if (!isRecord(item)) {
    return null;
  }

  const direct =
    mapDimensionValue(item.unitSize) ??
    mapDimensionValue(item.UnitSize) ??
    mapDimensionValue(item.size) ??
    mapDimensionValue(item.Size) ??
    mapDimensionValue(item.dimensions) ??
    mapDimensionValue(item.Dimensions);

  if (direct) {
    return direct;
  }

  const width = parseNullableNumericValue(
    item.finalUnitWidth ??
      item.FinalUnitWidth ??
      item.unitWidth ??
      item.UnitWidth ??
      item.width ??
      item.Width ??
      (isRecord(item.finalUnitWidth) ? item.finalUnitWidth.value : null) ??
      (isRecord(item.unitWidth) ? item.unitWidth.value : null),
  );
  const length = parseNullableNumericValue(
    item.finalUnitLength ??
      item.FinalUnitLength ??
      item.unitLength ??
      item.UnitLength ??
      item.length ??
      item.Length ??
      (isRecord(item.finalUnitLength) ? item.finalUnitLength.value : null) ??
      (isRecord(item.unitLength) ? item.unitLength.value : null),
  );
  const height = parseNullableNumericValue(
    item.finalUnitHeight ??
      item.FinalUnitHeight ??
      item.unitHeight ??
      item.UnitHeight ??
      item.height ??
      item.Height ??
      (isRecord(item.finalUnitHeight) ? item.finalUnitHeight.value : null) ??
      (isRecord(item.unitHeight) ? item.unitHeight.value : null),
  );

  if (width !== null || length !== null || height !== null) {
    return { width, length, height };
  }

  const parameterCollections = [
    item.parameters,
    item.Parameters,
    item.product?.parameters,
    item.Product?.parameters,
    item.product?.Parameters,
    item.Product?.Parameters,
    item.calculation?.parameters,
    item.Calculation?.parameters,
  ];

  for (const parameters of parameterCollections) {
    const parameterWidth = getParameterValueByCodes(parameters, [
      "finalUnitWidth",
      "unitWidth",
      "DieUnitWidth",
      "width",
    ]);
    const parameterLength = getParameterValueByCodes(parameters, [
      "finalUnitLength",
      "unitLength",
      "DieUnitLength",
      "length",
    ]);
    const parameterHeight = getParameterValueByCodes(parameters, [
      "finalUnitHeight",
      "unitHeight",
      "DieUnitHeight",
      "height",
    ]);

    if (
      parameterWidth !== null ||
      parameterLength !== null ||
      parameterHeight !== null
    ) {
      return {
        width: parameterWidth,
        length: parameterLength,
        height: parameterHeight,
      };
    }
  }

  const nestedProduct =
    mapDimensionValue(item.product?.unitSize) ??
    mapDimensionValue(item.Product?.unitSize) ??
    mapDimensionValue(item.product?.UnitSize) ??
    mapDimensionValue(item.Product?.UnitSize);

  return nestedProduct;
};

const toCompleteRequestUnitSize = (
  value: PackagingDimensionViewModel | null | undefined,
): PackagingDimensionViewModel | null => {
  if (!value) {
    return null;
  }

  const width = parseNullableNumericValue(value.width);
  const length = parseNullableNumericValue(value.length);
  const height = parseNullableNumericValue(value.height);

  if (width === null && length === null && height === null) {
    return null;
  }

  // API requires numeric object fields; fill only missing axes with 0.
  return {
    width: width ?? 0,
    length: length ?? 0,
    height: height ?? 0,
  };
};

const normalizeHierarchyNodes = (value: unknown): any[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (!isRecord(value)) {
    return [];
  }

  const nestedCollections = [
    value.rootPackages,
    value.RootPackages,
    value.packages,
    value.Packages,
    value.hierarchy,
    value.Hierarchy,
    value.subPackages,
    value.SubPackages,
  ];

  for (const collection of nestedCollections) {
    if (Array.isArray(collection)) {
      return collection;
    }
  }

  return [value];
};

const mapHierarchyItems = (
  value: unknown,
): PackagingHierarchyItemViewModel[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item, index) => ({
    id:
      firstNonEmptyValue(
        item?.id,
        item?.ID,
        item?.itemId,
        item?.ItemId,
        item?.documentItemId,
        item?.DocumentItemId,
      ) ?? `item-${index + 1}`,
    itemId:
      firstNonEmptyValue(
        item?.itemId,
        item?.ItemId,
        item?.documentItemId,
        item?.DocumentItemId,
      ) ?? null,
    name:
      firstNonEmptyValue(item?.name, item?.Name, item?.label, item?.Label) ??
      null,
    quantity: parseNullableNumericValue(item?.quantity ?? item?.Quantity),
    unitSize: mapDimensionValue(item?.unitSize ?? item?.UnitSize),
  }));
};

const mapHierarchyNode = (
  value: unknown,
  index: number,
  fallbackPackageName: (index: number) => string,
): PackagingHierarchyNodeViewModel => {
  const item = isRecord(value) ? value : {};
  const subPackages = normalizeHierarchyNodes(
    item.subPackages ?? item.SubPackages,
  ).map((subPackage, subPackageIndex) =>
    mapHierarchyNode(subPackage, subPackageIndex, fallbackPackageName),
  );
  const items = mapHierarchyItems(item.items ?? item.Items);
  const packageSourceType = resolvePackageSourceType(
    firstNonEmptyValue(item.packageSourceType, item.PackageSourceType),
  );
  const materialTypeKey = normalizeMaterialTypeKey(
    firstNonEmptyValue(item.materialTypeKey, item.MaterialTypeKey),
  );
  const categoryName =
    firstNonEmptyValue(item.categoryName, item.CategoryName) ?? null;
  const materialType =
    firstNonEmptyValue(item.materialType, item.MaterialType) ?? null;
  const sourceDocumentItemId =
    firstNonEmptyValue(
      item.sourceDocumentItemId,
      item.SourceDocumentItemId,
    ) ?? null;
  const resolvedPackageSourceType =
    packageSourceType === DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE ||
    Boolean(sourceDocumentItemId) ||
    isCartItemMaterialTypeKey(materialTypeKey)
      ? DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE
      : PREDEFINED_PACKAGE_SOURCE_TYPE;

  return {
    id:
      firstNonEmptyValue(
        item.id,
        item.ID,
        item.packageId,
        item.PackageId,
        item.key,
        item.Key,
      ) ?? `package-${index + 1}`,
    name:
      firstNonEmptyValue(
        item.name,
        item.Name,
        item.label,
        item.Label,
        item.categoryName,
        item.CategoryName,
        item.materialType,
        item.MaterialType,
        item.materialTypeName,
        item.MaterialTypeName,
        item.materialTypeKey,
        item.MaterialTypeKey,
      ) ?? fallbackPackageName(index + 1),
    quantity: parseNullableNumericValue(item.quantity ?? item.Quantity),
    packageSourceType: resolvedPackageSourceType,
    materialTypeKey:
      resolvedPackageSourceType === DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE
        ? CART_ITEM_PACKAGE_TYPE_KEY
        : materialTypeKey,
    categoryName:
      resolvedPackageSourceType === DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE
        ? null
        : categoryName,
    materialType:
      resolvedPackageSourceType === DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE
        ? null
        : materialType,
    printHouseMaterialId:
      resolvedPackageSourceType === DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE
        ? null
        : firstNonEmptyValue(
            item.printHouseMaterialId,
            item.PrintHouseMaterialId,
          ) ?? null,
    sourceDocumentItemId:
      resolvedPackageSourceType === DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE
        ? sourceDocumentItemId
        : null,
    unitSize: mapDimensionValue(item.unitSize ?? item.UnitSize),
    resolvedPackedSize: mapDimensionValue(
      item.resolvedPackedSize ?? item.ResolvedPackedSize,
    ),
    fits:
      typeof (item.fits ?? item.Fits) === "boolean"
        ? Boolean(item.fits ?? item.Fits)
        : undefined,
    unitPrice: parseNullableNumericValue(item.unitPrice ?? item.UnitPrice),
    calculatedUnitPrice: parseNullableNumericValue(
      item.calculatedUnitPrice ?? item.CalculatedUnitPrice,
    ),
    isDefault: parseExplicitBoolean(item.isDefault ?? item.IsDefault),
    materialUnitCost: parseNullableNumericValue(
      item.materialUnitCost ?? item.MaterialUnitCost,
    ),
    unitLaborTimeInMin: parseNullableNumericValue(
      item.unitLaborTimeInMin ?? item.UnitLaborTimeInMin,
    ),
    unitLaborCosts: parseNullableNumericValue(
      item.unitLaborCosts ?? item.UnitLaborCosts,
    ),
    directItemsCount: items.length,
    childPackagesCount: subPackages.length,
    items,
    subPackages,
  };
};

const mapPackagingHierarchyNodes = (
  value: unknown,
  fallbackPackageName: (index: number) => string,
): PackagingHierarchyNodeViewModel[] =>
  normalizeHierarchyNodes(value).map((node, index) =>
    mapHierarchyNode(node, index, fallbackPackageName),
  );

const createEmptyPackagingHierarchy = (
  context?: Partial<PackagingSourceContext>,
): PackagingHierarchyViewModel => ({
  id: null,
  entityType: context?.entityType ?? null,
  entityId: context?.entityId ?? null,
  exists: false,
  rootPackages: [],
});

const mapPackagingHierarchyResponse = (
  response: any,
  context: PackagingSourceContext,
  fallbackPackageName: (index: number) => string,
): PackagingHierarchyViewModel => {
  if (!response || typeof response !== "object") {
    return createEmptyPackagingHierarchy(context);
  }

  const rootPackages = mapPackagingHierarchyNodes(
    response?.hierarchy ?? response?.Hierarchy,
    fallbackPackageName,
  );
  const inferredEntityType = inferPackagingEntityType(response);
  const inferredEntityId =
    firstNonEmptyValue(
      response?.entityId,
      response?.EntityId,
      response?.QuoteID,
      response?.quoteId,
      response?.OrderID,
      response?.orderId,
      response?.InvoiceID,
      response?.invoiceId,
      response?.DeliveryNoteID,
      response?.deliveryNoteId,
    ) ?? null;
  const cartDisplayMode = parseCartDisplayMode(
    response?.cartDisplayMode ?? response?.CartDisplayMode,
  );

  const isPackageOptimizedRaw =
    response?.isPackageOptimized ?? response?.IsPackageOptimized;
  const isPackageOptimized =
    typeof isPackageOptimizedRaw === "boolean"
      ? isPackageOptimizedRaw
      : undefined;

  return {
    id: firstNonEmptyValue(response?.id, response?.ID) ?? null,
    entityType: inferredEntityType ?? context.entityType,
    entityId: inferredEntityId ?? context.entityId ?? null,
    exists:
      typeof response?.exists === "boolean"
        ? response.exists
        : Boolean(response?.id ?? response?.ID ?? inferredEntityId) ||
          rootPackages.length > 0,
    ...(isPackageOptimized !== undefined ? { isPackageOptimized } : {}),
    ...(cartDisplayMode ? { cartDisplayMode } : {}),
    rootPackages,
  };
};

const inferPackagingEntityType = (
  response: Record<string, unknown>,
): PackagingEntityType | undefined => {
  const explicit = firstNonEmptyValue(response?.entityType);
  if (isPackagingDocumentKind(explicit)) {
    return explicit;
  }
  if (firstNonEmptyValue(response?.QuoteID, response?.quoteId)) {
    return "quote";
  }
  if (firstNonEmptyValue(response?.OrderID, response?.orderId)) {
    return "order";
  }
  if (firstNonEmptyValue(response?.InvoiceID, response?.invoiceId)) {
    return "invoice";
  }
  if (
    firstNonEmptyValue(response?.DeliveryNoteID, response?.deliveryNoteId)
  ) {
    return "deliveryNote";
  }
  return undefined;
};

const parseExplicitBoolean = (value: unknown): boolean | undefined => {
  if (value === true || value === false) {
    return value;
  }
  return undefined;
};

const hierarchyHasDefaultPricing = (
  nodes: PackagingHierarchyNodeViewModel[],
): boolean =>
  nodes.some(
    (node) =>
      node.isDefault === true || hierarchyHasDefaultPricing(node.subPackages),
  );

const hasCompleteUnitSize = (
  value: PackagingDimensionViewModel | null | undefined,
): value is PackagingDimensionViewModel => {
  if (!value) {
    return false;
  }

  return (
    parseNullableNumericValue(value.width) !== null ||
    parseNullableNumericValue(value.length) !== null ||
    parseNullableNumericValue(value.height) !== null
  );
};

/** Copy known hierarchy/assignment sizes onto document items so later reassignment keeps them. */
const enrichDocumentItemsWithKnownUnitSizes = (
  documentItems: PackagingItemViewModel[],
  packagesById: Record<string, PackagingBuilderPackageState>,
): PackagingItemViewModel[] => {
  const knownUnitSizesByDocumentItemId: Record<
    string,
    PackagingDimensionViewModel
  > = {};

  Object.values(packagesById).forEach((packageState) => {
    packageState.assignedItems.forEach((assignedItem) => {
      if (!hasCompleteUnitSize(assignedItem.unitSize)) {
        return;
      }

      const candidateIds = [
        assignedItem.documentItemId,
        assignedItem.id,
      ].filter((id): id is string => Boolean(id));

      candidateIds.forEach((candidateId) => {
        if (!knownUnitSizesByDocumentItemId[candidateId]) {
          knownUnitSizesByDocumentItemId[candidateId] = assignedItem.unitSize;
        }
      });
    });
  });

  if (Object.keys(knownUnitSizesByDocumentItemId).length === 0) {
    return documentItems;
  }

  return documentItems.map((documentItem) => {
    if (hasCompleteUnitSize(documentItem.unitSize)) {
      return documentItem;
    }

    const knownUnitSize = knownUnitSizesByDocumentItemId[documentItem.id];
    if (!knownUnitSize) {
      return documentItem;
    }

    return {
      ...documentItem,
      unitSize: knownUnitSize,
    };
  });
};

/**
 * A synthesized default package copies calculatedUnitPrice into unitPrice.
 * That echo is not a saved or manual price, so the editable price starts at 0.
 * A unit price that differs from the calculated price is a real override.
 */
const isSynthesizedDefaultUnitPrice = (node: {
  isDefault?: boolean;
  unitPrice?: number | null;
  calculatedUnitPrice?: number | null;
}): boolean => {
  if (node.isDefault !== true) {
    return false;
  }

  const unitPrice = node.unitPrice ?? null;
  if (unitPrice === null) {
    return true;
  }

  return unitPrice === (node.calculatedUnitPrice ?? null);
};

const resolveMappedUnitPrice = (node: {
  isDefault?: boolean;
  unitPrice?: number | null;
  calculatedUnitPrice?: number | null;
}): number | null =>
  isSynthesizedDefaultUnitPrice(node) ? 0 : (node.unitPrice ?? null);

/**
 * Match, calculate, and optimize replace builder packages from an API payload.
 * Keep a manual unit price when the same package comes back.
 */
const preserveManuallyEditedUnitPrices = (
  incoming: Record<string, PackagingBuilderPackageState>,
  previous: Record<string, PackagingBuilderPackageState> | undefined,
): Record<string, PackagingBuilderPackageState> => {
  if (!previous) {
    return incoming;
  }

  return Object.fromEntries(
    Object.entries(incoming).map(([packageId, packageState]) => {
      const previousPackage = previous[packageId];
      if (previousPackage?.priceEdited !== true) {
        return [packageId, packageState];
      }

      return [
        packageId,
        {
          ...packageState,
          unitPrice: previousPackage.unitPrice ?? 0,
          priceEdited: true,
        },
      ];
    }),
  );
};

const normalizePackagingHierarchyBuilderState = (
  packaging: PackagingHierarchyViewModel,
  documentItems: PackagingItemViewModel[],
): Omit<PackagingHierarchyBuilderState, "activePackageId"> => {
  const packagesById: Record<string, PackagingBuilderPackageState> = {};
  const rootPackageIds: string[] = [];

  const registerNode = (
    node: PackagingHierarchyNodeViewModel,
    parentId: string | null,
    depth: number,
  ) => {
    const subPackageIds = node.subPackages.map((subPackage) => subPackage.id);

    packagesById[node.id] = {
      id: node.id,
      name: node.name,
      quantity: Math.max(1, node.quantity ?? 1),
      parentId,
      subPackageIds,
      depth,
      packageSourceType: node.packageSourceType,
      materialTypeKey: node.materialTypeKey,
      categoryName: node.categoryName,
      materialType: node.materialType,
      printHouseMaterialId: node.printHouseMaterialId,
      sourceDocumentItemId: node.sourceDocumentItemId,
      unitSize: node.unitSize,
      resolvedPackedSize: node.resolvedPackedSize,
      fits: node.fits,
      unitPrice: resolveMappedUnitPrice(node),
      calculatedUnitPrice: node.calculatedUnitPrice ?? null,
      isDefault: node.isDefault,
      materialUnitCost: node.materialUnitCost ?? null,
      unitLaborTimeInMin: node.unitLaborTimeInMin ?? null,
      unitLaborCosts: node.unitLaborCosts ?? null,
      assignedItems: node.items.map((item) => {
        const documentItemId = item.itemId ?? item.id;
        const documentItem = documentItems.find(
          (candidate) => candidate.id === documentItemId,
        );
        return {
          id: item.id,
          documentItemId,
          name: item.name ?? documentItem?.name ?? null,
          quantity: item.quantity ?? 0,
          unitSize: item.unitSize ?? documentItem?.unitSize ?? null,
        };
      }),
    };

    if (parentId === null) {
      rootPackageIds.push(node.id);
    }

    node.subPackages.forEach((subPackage) =>
      registerNode(subPackage, node.id, depth + 1),
    );
  };

  packaging.rootPackages.forEach((rootPackage) =>
    registerNode(rootPackage, null, 0),
  );

  Object.values(packagesById).forEach((packageState) => {
    if (
      packageState.packageSourceType !== DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE ||
      !packageState.sourceDocumentItemId
    ) {
      return;
    }

    const documentItem = documentItems.find(
      (item) => item.id === packageState.sourceDocumentItemId,
    );
    if (!documentItem) {
      return;
    }

    packageState.name = formatDocumentItemSubtypeLabel(documentItem);
    packageState.materialType = documentItem.name;
    packageState.materialTypeKey = CART_ITEM_PACKAGE_TYPE_KEY;
  });

  const enrichedDocumentItems = enrichDocumentItemsWithKnownUnitSizes(
    documentItems,
    packagesById,
  );

  // Re-apply enriched document sizes onto assignments that still miss unitSize.
  Object.values(packagesById).forEach((packageState) => {
    packageState.assignedItems = packageState.assignedItems.map(
      (assignedItem) => {
        if (hasCompleteUnitSize(assignedItem.unitSize)) {
          return assignedItem;
        }

        const documentItem = enrichedDocumentItems.find(
          (item) =>
            item.id === assignedItem.documentItemId ||
            item.id === assignedItem.id,
        );

        return {
          ...assignedItem,
          unitSize: documentItem?.unitSize ?? assignedItem.unitSize,
        };
      },
    );
  });

  return {
    packagesById,
    rootPackageIds,
    documentItems: enrichedDocumentItems,
  };
};

const getDefaultActivePackageId = (
  builderState: Omit<PackagingHierarchyBuilderState, "activePackageId">,
): string | null =>
  builderState.rootPackageIds[0] ??
  Object.keys(builderState.packagesById)[0] ??
  null;

const getPackageAssignedItemCount = (
  packageState: PackagingBuilderPackageState,
): number =>
  packageState.assignedItems.filter((item) => item.quantity > 0).length;

/** Direct child ids recursively (excludes `packageId` itself). */
const getPackageDescendantIds = (
  packagesById: Record<string, PackagingBuilderPackageState>,
  packageId: string,
): Set<string> => {
  const descendantIds = new Set<string>();

  const collectDescendants = (currentPackageId: string) => {
    const currentPackage = packagesById[currentPackageId];
    currentPackage?.subPackageIds.forEach((subPackageId) => {
      if (descendantIds.has(subPackageId)) {
        return;
      }

      descendantIds.add(subPackageId);
      collectDescendants(subPackageId);
    });
  };

  collectDescendants(packageId);
  return descendantIds;
};

const getPackageEffectiveQuantity = (
  packageState: PackagingBuilderPackageState | null | undefined,
  packagesById: Record<string, PackagingBuilderPackageState>,
): number => {
  if (!packageState) {
    return 1;
  }

  let effectiveQuantity = 1;
  let currentPackage: PackagingBuilderPackageState | null | undefined =
    packageState;
  const visitedPackageIds = new Set<string>();

  while (currentPackage && !visitedPackageIds.has(currentPackage.id)) {
    visitedPackageIds.add(currentPackage.id);
    effectiveQuantity *= Math.max(1, currentPackage.quantity ?? 1);
    currentPackage = currentPackage.parentId
      ? packagesById[currentPackage.parentId]
      : null;
  }

  return effectiveQuantity;
};

const getPackageTotalPrice = (
  packageState: PackagingBuilderPackageState | null | undefined,
  packagesById: Record<string, PackagingBuilderPackageState>,
): number =>
  (packageState?.unitPrice ?? 0) *
  getPackageEffectiveQuantity(packageState, packagesById);

const getPackagesTotalPrice = (
  builderState: PackagingHierarchyBuilderState,
): number =>
  Object.values(builderState.packagesById).reduce(
    (total, packageState) =>
      total + getPackageTotalPrice(packageState, builderState.packagesById),
    0,
  );

const getPackageTotalCost = (
  packageState: PackagingBuilderPackageState | null | undefined,
  packagesById: Record<string, PackagingBuilderPackageState>,
): number =>
  ((packageState?.unitLaborCosts ?? 0) +
    (packageState?.materialUnitCost ?? 0)) *
  getPackageEffectiveQuantity(packageState, packagesById);

const getDisplayedUnitPrice = (
  packageState: PackagingBuilderPackageState | null | undefined,
  mode: PackagingPriceDisplayMode,
): number => {
  if (
    mode === "defaultZero" &&
    packageState?.isDefault === true &&
    !packageState.priceEdited
  ) {
    return 0;
  }
  return packageState?.unitPrice ?? 0;
};

const getDisplayedPackageTotalPrice = (
  packageState: PackagingBuilderPackageState | null | undefined,
  packagesById: Record<string, PackagingBuilderPackageState>,
  mode: PackagingPriceDisplayMode,
): number =>
  getDisplayedUnitPrice(packageState, mode) *
  getPackageEffectiveQuantity(packageState, packagesById);

const getDisplayedPackagesTotalPrice = (
  builderState: PackagingHierarchyBuilderState,
  mode: PackagingPriceDisplayMode,
): number =>
  Object.values(builderState.packagesById).reduce(
    (total, packageState) =>
      total +
      getDisplayedPackageTotalPrice(
        packageState,
        builderState.packagesById,
        mode,
      ),
    0,
  );

const builderHasEditedPrices = (
  builderState: PackagingHierarchyBuilderState,
): boolean =>
  Object.values(builderState.packagesById).some(
    (packageState) => packageState.priceEdited === true,
  );

const withDisplayedUnitPrices = (
  builderState: PackagingHierarchyBuilderState,
  mode: PackagingPriceDisplayMode,
): PackagingHierarchyBuilderState => ({
  ...builderState,
  packagesById: Object.fromEntries(
    Object.entries(builderState.packagesById).map(([packageId, packageState]) => [
      packageId,
      {
        ...packageState,
        unitPrice: getDisplayedUnitPrice(packageState, mode),
      },
    ]),
  ),
});

const shouldOmitDefaultPackagingSave = (
  mode: PackagingPriceDisplayMode,
  builderState: PackagingHierarchyBuilderState,
  hierarchyId: string | null | undefined,
): boolean =>
  mode === "defaultZero" &&
  !builderHasEditedPrices(builderState) &&
  !hierarchyId;

const getPackagesTotalCost = (
  builderState: PackagingHierarchyBuilderState,
): number =>
  Object.values(builderState.packagesById).reduce(
    (total, packageState) =>
      total + getPackageTotalCost(packageState, builderState.packagesById),
    0,
  );

const getPackageAssignedItemQuantity = (
  packageState: PackagingBuilderPackageState | null | undefined,
  documentItemId: string,
): number =>
  packageState?.assignedItems.find(
    (assignedItem) => assignedItem.documentItemId === documentItemId,
  )?.quantity ?? 0;

/** Effective usage of a document item as Cart Item package containers. */
const getDocumentItemCartPackageUsageQuantity = (
  builderState: PackagingHierarchyBuilderState,
  documentItemId: string,
): number =>
  Object.values(builderState.packagesById).reduce((total, packageState) => {
    if (
      !isDocumentItemPackage(packageState) ||
      packageState.sourceDocumentItemId !== documentItemId
    ) {
      return total;
    }

    return (
      total + getPackageEffectiveQuantity(packageState, builderState.packagesById)
    );
  }, 0);

const getDocumentItemTotalAssignedQuantity = (
  builderState: PackagingHierarchyBuilderState,
  documentItemId: string,
): number => {
  const assignedAsContents = Object.values(builderState.packagesById).reduce(
    (total, packageState) => {
      const assignedQuantity = getPackageAssignedItemQuantity(
        packageState,
        documentItemId,
      );
      return (
        total +
        assignedQuantity *
          getPackageEffectiveQuantity(packageState, builderState.packagesById)
      );
    },
    0,
  );

  return (
    assignedAsContents +
    getDocumentItemCartPackageUsageQuantity(builderState, documentItemId)
  );
};

export type AssignedItemsPackagingValidationResult =
  | { ok: true }
  | {
      ok: false;
      messageKey: string;
      packageIdsWithWarning?: string[];
    };

/**
 * Ensures every package (including autofill) has content before match,
 * calculate, or submit: either a document line with quantity > 0, or at
 * least one sub-package (items may live only on nested packages).
 * Document lines may intentionally remain unpacked.
 */
const validateAssignedItemsForPackagingOperations = (
  builderState: PackagingHierarchyBuilderState,
): AssignedItemsPackagingValidationResult => {
  const packageIdsMissingItemAssignments: string[] = [];

  for (const packageState of Object.values(builderState.packagesById)) {
    const hasPositiveAssignment = packageState.assignedItems.some(
      (assignedItem) =>
        Boolean(assignedItem.documentItemId) && assignedItem.quantity > 0,
    );
    const hasSubPackages = packageState.subPackageIds.some((subPackageId) =>
      Boolean(builderState.packagesById[subPackageId]),
    );

    if (!hasPositiveAssignment && !hasSubPackages) {
      packageIdsMissingItemAssignments.push(packageState.id);
    }
  }

  if (packageIdsMissingItemAssignments.length > 0) {
    return {
      ok: false as const,
      messageKey:
        "packagingAndShipping.assignedItemsPackageEmptyValidationMessage",
      packageIdsWithWarning: packageIdsMissingItemAssignments,
    };
  }

  for (const packageState of Object.values(builderState.packagesById)) {
    if (!isDocumentItemPackage(packageState)) {
      continue;
    }

    const sourceDocumentItemId = packageState.sourceDocumentItemId;
    if (!sourceDocumentItemId) {
      return {
        ok: false as const,
        messageKey:
          "packagingAndShipping.selectCartItemSubtypeBeforeApiMessage",
        packageIdsWithWarning: [packageState.id],
      };
    }

    const documentItem = builderState.documentItems.find(
      (item) => item.id === sourceDocumentItemId,
    );
    if (!documentItem) {
      return {
        ok: false as const,
        messageKey: "packagingAndShipping.cartItemSourceMissingMessage",
        packageIdsWithWarning: [packageState.id],
      };
    }

    const totalUsage = getDocumentItemTotalAssignedQuantity(
      builderState,
      sourceDocumentItemId,
    );
    if (totalUsage > documentItem.totalQuantity) {
      return {
        ok: false as const,
        messageKey: "packagingAndShipping.cartItemQuantityExceededMessage",
        packageIdsWithWarning: [packageState.id],
      };
    }

    const selfAssignedQuantity = getPackageAssignedItemQuantity(
      packageState,
      sourceDocumentItemId,
    );
    if (selfAssignedQuantity > 0) {
      return {
        ok: false as const,
        messageKey: "packagingAndShipping.cartItemSelfAssignmentMessage",
        packageIdsWithWarning: [packageState.id],
      };
    }
  }

  return { ok: true as const };
};

const getMaxAssignableQuantityForPackageItem = (
  builderState: PackagingHierarchyBuilderState,
  packageState: PackagingBuilderPackageState | null,
  documentItem: PackagingItemViewModel,
): number => {
  if (!packageState) {
    return 0;
  }

  if (
    isDocumentItemPackage(packageState) &&
    packageState.sourceDocumentItemId === documentItem.id
  ) {
    return 0;
  }

  const currentPackageAssignedTotal =
    getPackageAssignedItemQuantity(packageState, documentItem.id) *
    getPackageEffectiveQuantity(packageState, builderState.packagesById);
  const totalAssignedAcrossPackages = getDocumentItemTotalAssignedQuantity(
    builderState,
    documentItem.id,
  );
  const otherPackagesAssignedTotal = Math.max(
    totalAssignedAcrossPackages - currentPackageAssignedTotal,
    0,
  );
  const availableForActivePackage = Math.max(
    documentItem.totalQuantity - otherPackagesAssignedTotal,
    0,
  );

  return Math.floor(
    availableForActivePackage /
      getPackageEffectiveQuantity(packageState, builderState.packagesById),
  );
};

/** Max package quantity for a Cart Item package given source-item remaining capacity. */
const getMaxCartItemPackageQuantity = (
  builderState: PackagingHierarchyBuilderState,
  packageId: string,
): number => {
  const packageState = builderState.packagesById[packageId];
  if (!isDocumentItemPackage(packageState) || !packageState?.sourceDocumentItemId) {
    return Number.MAX_SAFE_INTEGER;
  }

  const documentItem = builderState.documentItems.find(
    (item) => item.id === packageState.sourceDocumentItemId,
  );
  if (!documentItem) {
    return 0;
  }

  const parentEffectiveQuantity = packageState.parentId
    ? getPackageEffectiveQuantity(
        builderState.packagesById[packageState.parentId],
        builderState.packagesById,
      )
    : 1;

  const currentPackageUsage =
    Math.max(1, packageState.quantity ?? 1) * parentEffectiveQuantity;
  const totalUsage = getDocumentItemTotalAssignedQuantity(
    builderState,
    packageState.sourceDocumentItemId,
  );
  const otherUsage = Math.max(totalUsage - currentPackageUsage, 0);
  const available = Math.max(documentItem.totalQuantity - otherUsage, 0);

  return Math.max(0, Math.floor(available / parentEffectiveQuantity));
};

const getPackageParentLabel = (
  packageState: PackagingBuilderPackageState,
  packagesById: Record<string, PackagingBuilderPackageState>,
): string | null => {
  if (!packageState.parentId) {
    return null;
  }

  return packagesById[packageState.parentId]?.name ?? null;
};

const getActivePackageDocumentItems = (
  documentItems: PackagingItemViewModel[],
  activePackage: PackagingBuilderPackageState | null,
  builderState: PackagingHierarchyBuilderState,
): PackagingBuilderDocumentItemViewModel[] =>
  documentItems.map((item) => {
    const isSourceOfActivePackage =
      isDocumentItemPackage(activePackage) &&
      activePackage?.sourceDocumentItemId === item.id;
    const assignedQuantity = getPackageAssignedItemQuantity(
      activePackage,
      item.id,
    );
    const assignedTotalQuantity =
      assignedQuantity *
      getPackageEffectiveQuantity(activePackage, builderState.packagesById);
    const totalAssignedQuantity = getDocumentItemTotalAssignedQuantity(
      builderState,
      item.id,
    );

    return {
      ...item,
      assignedQuantity,
      assignedTotalQuantity,
      maxAssignableQuantity: isSourceOfActivePackage
        ? 0
        : getMaxAssignableQuantityForPackageItem(
            builderState,
            activePackage,
            item,
          ),
      remainingQuantity: Math.max(
        item.totalQuantity - totalAssignedQuantity,
        0,
      ),
      isSourceOfActivePackage,
    };
  });

const buildPackageMatchRequestNodes = (
  builderState: PackagingHierarchyBuilderState,
): PackagingPackageMatchRequestNode[] => {
  const documentItemsById = Object.fromEntries(
    builderState.documentItems.map((documentItem) => [
      documentItem.id,
      documentItem,
    ]),
  );

  const resolveAssignedItemUnitSize = (
    assignedItem: PackagingBuilderPackageState["assignedItems"][number],
  ): PackagingDimensionViewModel | null => {
    if (hasCompleteUnitSize(assignedItem.unitSize)) {
      return toCompleteRequestUnitSize(assignedItem.unitSize);
    }

    const documentItemId = assignedItem.documentItemId ?? assignedItem.id;
    const documentItem = documentItemId
      ? documentItemsById[documentItemId]
      : null;
    if (hasCompleteUnitSize(documentItem?.unitSize)) {
      return toCompleteRequestUnitSize(documentItem.unitSize);
    }

    for (const packageState of Object.values(builderState.packagesById)) {
      const matchedAssignedItem = packageState.assignedItems.find(
        (candidate) =>
          hasCompleteUnitSize(candidate.unitSize) &&
          ((documentItemId &&
            (candidate.documentItemId === documentItemId ||
              candidate.id === documentItemId)) ||
            candidate.id === assignedItem.id),
      );
      if (matchedAssignedItem?.unitSize) {
        return toCompleteRequestUnitSize(matchedAssignedItem.unitSize);
      }
    }

    return null;
  };

  const buildNode = (packageId: string): PackagingPackageMatchRequestNode => {
    const packageState = builderState.packagesById[packageId];
    const isCartItemPackage = isDocumentItemPackage(packageState);

    return {
      id: packageState.id,
      packageSourceType: isCartItemPackage
        ? DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE
        : PREDEFINED_PACKAGE_SOURCE_TYPE,
      materialTypeKey: isCartItemPackage ? null : packageState.materialTypeKey,
      categoryName: isCartItemPackage ? null : packageState.categoryName,
      materialType: isCartItemPackage ? null : packageState.materialType,
      printHouseMaterialId: isCartItemPackage
        ? null
        : packageState.printHouseMaterialId,
      sourceDocumentItemId: isCartItemPackage
        ? packageState.sourceDocumentItemId
        : null,
      quantity: Math.max(1, packageState.quantity ?? 1),
      unitSize: toCompleteRequestUnitSize(packageState.unitSize),
      resolvedPackedSize: toCompleteRequestUnitSize(
        packageState.resolvedPackedSize,
      ),
      items: packageState.assignedItems
        .filter((item) => item.quantity > 0)
        .map((item) => {
          const resolvedUnitSize = resolveAssignedItemUnitSize(item);
          return {
            id: item.id,
            quantity: item.quantity,
            // Keep a numeric object for current packaging-service validation.
            // Prefer real document-item dimensions; only use zeros when none exist.
            unitSize: resolvedUnitSize ?? { width: 0, length: 0, height: 0 },
          };
        }),
      subPackages: packageState.subPackageIds.map(buildNode),
      fits: packageState.fits,
      unitPrice: packageState.unitPrice ?? null,
      calculatedUnitPrice: packageState.calculatedUnitPrice ?? null,
      materialUnitCost: packageState.materialUnitCost ?? null,
      unitLaborTimeInMin: packageState.unitLaborTimeInMin ?? null,
      unitLaborCosts: packageState.unitLaborCosts ?? null,
    };
  };

  return builderState.rootPackageIds.map(buildNode);
};

export type PackageMaterialSelectionValidationResult =
  | { ok: true }
  | { ok: false; messageKey: string };

/**
 * Type/Subtype are only required for actions that call the match/calculate
 * APIs. Saving intentionally accepts empty values, so this is not used there.
 * Reports Type, Subtype or both so the user sees one accurate message.
 */
const validatePackageMaterialSelectionsForMatchOrCalculate = (
  builderState: PackagingHierarchyBuilderState,
  options: {
    autofillTypeKey: string;
    autofillSubtypeName: string;
    materialTypeOptions: Array<{
      materialTypeKey: string;
      materials?: unknown[];
    }>;
  },
): PackageMaterialSelectionValidationResult => {
  const autofillKeyNorm = normalizeMaterialTypeKey(options.autofillTypeKey);
  let isTypeMissing = false;
  let isSubtypeMissing = false;

  for (const packageState of Object.values(builderState.packagesById)) {
    const typeKeyNorm = normalizeMaterialTypeKey(packageState.materialTypeKey);
    const isCartItemPackage =
      packageState.packageSourceType === DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE ||
      isCartItemMaterialTypeKey(packageState.materialTypeKey);

    if (!typeKeyNorm) {
      isTypeMissing = true;
      // A subtype cannot be chosen without a type, so it is missing as well
      // unless one was somehow preserved.
      if (
        packageState.printHouseMaterialId == null &&
        !packageState.sourceDocumentItemId
      ) {
        isSubtypeMissing = true;
      }
      continue;
    }

    if (isCartItemPackage) {
      if (!packageState.sourceDocumentItemId) {
        isSubtypeMissing = true;
      }
      continue;
    }

    if (typeKeyNorm === autofillKeyNorm) {
      continue;
    }

    const selectedTypeOption =
      options.materialTypeOptions.find(
        (option) =>
          normalizeMaterialTypeKey(option.materialTypeKey) === typeKeyNorm,
      ) ?? null;

    if ((selectedTypeOption?.materials?.length ?? 0) === 0) {
      continue;
    }

    const isAutofillSubtypeSelection =
      packageState.materialType === options.autofillSubtypeName;

    if (
      packageState.printHouseMaterialId == null &&
      !isAutofillSubtypeSelection
    ) {
      isSubtypeMissing = true;
    }
  }

  if (isTypeMissing && isSubtypeMissing) {
    return {
      ok: false,
      messageKey:
        "packagingAndShipping.selectPackageTypeAndSubtypeBeforeApiMessage",
    };
  }
  if (isTypeMissing) {
    return {
      ok: false,
      messageKey: "packagingAndShipping.selectPackageTypeBeforeApiMessage",
    };
  }
  if (isSubtypeMissing) {
    return {
      ok: false,
      messageKey: "packagingAndShipping.selectPackageSubtypeBeforeApiMessage",
    };
  }

  return { ok: true };
};

type PackagingSubtypeOptionGroup = {
  categoryKey: string;
  categoryName: string;
  options: PackagingMaterialSubtypeOption[];
};

/**
 * Compact "length × width × height" label for a subtype option.
 * Some material types already use a dimension as the option name, so a label
 * identical to the name is dropped instead of being rendered twice.
 */
const formatPackagingSubtypeDimensions = (
  option: PackagingMaterialSubtypeOption | null | undefined,
): string => {
  if (!option?.dimensions) {
    return "";
  }

  const { length, width, height } = option.dimensions;
  const label = [length, width, height]
    .filter((axis): axis is number => axis !== null && Number.isFinite(axis))
    .map((axis) => Number(axis.toFixed(2)).toString())
    .join(" × ");

  return label && label !== option.materialType?.trim() ? label : "";
};

/** Group subtype options by category for the 2-level tree dropdown. */
const groupPackagingSubtypeOptionsByCategory = (
  options: PackagingMaterialSubtypeOption[],
): PackagingSubtypeOptionGroup[] => {
  const groupsByCategory = new Map<string, PackagingSubtypeOptionGroup>();

  for (const option of options) {
    const categoryKey =
      option.categoryKey?.trim() || option.categoryName?.trim() || "uncategorized";
    const categoryName =
      option.categoryName?.trim() ||
      option.categoryKey?.trim() ||
      "Uncategorized";
    const existing = groupsByCategory.get(categoryKey);

    if (existing) {
      existing.options.push(option);
    } else {
      groupsByCategory.set(categoryKey, {
        categoryKey,
        categoryName,
        options: [option],
      });
    }
  }

  return Array.from(groupsByCategory.values()).sort((left, right) =>
    left.categoryName.localeCompare(right.categoryName, undefined, {
      sensitivity: "base",
    }),
  );
};

const mapPackagingMaterialTypeOptions = (
  value: unknown,
): PackagingMaterialTypeOption[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map<PackagingMaterialTypeOption | null>((entry, index) => {
      if (!isRecord(entry)) {
        return null;
      }

      const materialTypeKey =
        normalizeMaterialTypeKey(
          firstNonEmptyValue(entry.materialTypeKey, entry.MaterialTypeKey),
        ) ?? `type-${index + 1}`;
      const materialTypeName =
        firstNonEmptyValue(entry.materialTypeName, entry.MaterialTypeName) ??
        materialTypeKey;
      const materials = Array.isArray(entry.materials)
        ? entry.materials
            .map<PackagingMaterialTypeOption["materials"][number] | null>(
              (material) => {
                if (!isRecord(material)) {
                  return null;
                }

                const printHouseMaterialId =
                  firstNonEmptyValue(
                    material.printHouseMaterialId,
                    material.PrintHouseMaterialId,
                    material.id,
                    material.ID,
                    material.Id,
                  ) ?? null;

                if (!printHouseMaterialId) {
                  return null;
                }

                return {
                  printHouseMaterialId,
                  materialType:
                    firstNonEmptyValue(
                      material.materialType,
                      material.MaterialType,
                      material.name,
                      material.Name,
                      material.title,
                      material.Title,
                    ) ?? printHouseMaterialId,
                  categoryKey:
                    firstNonEmptyValue(
                      material.categoryKey,
                      material.CategoryKey,
                    ) ?? null,
                  categoryName:
                    firstNonEmptyValue(
                      material.categoryName,
                      material.CategoryName,
                    ) ?? null,
                  dimensions: mapDimensionValue(material),
                };
              },
            )
            .filter(
              (
                material,
              ): material is PackagingMaterialTypeOption["materials"][number] =>
                material !== null,
            )
            .sort((left, right) => {
              const leftCategory = (left.categoryName ?? "").trim();
              const rightCategory = (right.categoryName ?? "").trim();
              const categoryCompare = leftCategory.localeCompare(
                rightCategory,
                undefined,
                { sensitivity: "base" },
              );
              if (categoryCompare !== 0) {
                return categoryCompare;
              }
              return (left.materialType ?? "").localeCompare(
                right.materialType ?? "",
                undefined,
                { sensitivity: "base" },
              );
            })
        : [];

      return {
        materialTypeKey,
        materialTypeName,
        materials,
      };
    })
    .filter((entry): entry is PackagingMaterialTypeOption => entry !== null);
};

const mapDocumentItemsToPackagingItems = (
  documentItems: any[] | undefined,
  fallbackItemName: (index: number) => string,
): PackagingItemViewModel[] => {
  if (!Array.isArray(documentItems)) {
    return [];
  }

  return documentItems.reduce<PackagingItemViewModel[]>((acc, item, index) => {
    if (!shouldShowDocumentItemInPackagingItems(item)) {
      return acc;
    }

    const itemCode = firstNonEmptyValue(
      item?.productCode,
      item?.code,
      item?.sku,
      item?.itemCode,
      item?.catalogNumber,
    );
    const jobName = firstNonEmptyValue(
      item?.workName,
      item?.jobName,
      item?.WorkName,
      item?.JobName,
    );

    const quantityValue = item?.quantity;
    const parsedQuantity = parseNumericValue(quantityValue);

    acc.push({
      id: String(item?.id ?? `${index + 1}`),
      lineNumber: index + 1,
      name:
        firstNonEmptyValue(
          item?.productName,
          item?.itemName,
          item?.title,
          item?.name,
        ) ?? fallbackItemName(index + 1),
      code: itemCode,
      jobName,
      totalQuantity: parsedQuantity,
      totalQuantityLabel:
        quantityValue != null && String(quantityValue).trim() !== ""
          ? String(quantityValue)
          : String(parsedQuantity),
      unitSize: extractDocumentItemUnitSize(item),
    });

    return acc;
  }, []);
};

export {
  CART_ITEM_PACKAGE_TYPE_KEY,
  DOCUMENT_ITEM_PACKAGE_SOURCE_TYPE,
  PREDEFINED_PACKAGE_SOURCE_TYPE,
  isCartItemMaterialTypeKey,
  isCartonMaterialTypeKey,
  normalizeMaterialTypeKey,
  enrichDocumentItemsWithKnownUnitSizes,
  hasCompleteUnitSize,
  groupPackagingSubtypeOptionsByCategory,
  buildPackageMatchRequestNodes,
  createEmptyPackagingHierarchy,
  formatDocumentItemSubtypeLabel,
  formatPackagingSubtypeDimensions,
  getActivePackageDocumentItems,
  validateAssignedItemsForPackagingOperations,
  validatePackageMaterialSelectionsForMatchOrCalculate,
  getDocumentItemCartPackageUsageQuantity,
  getDocumentItemTotalAssignedQuantity,
  getDefaultActivePackageId,
  getMaxAssignableQuantityForPackageItem,
  getMaxCartItemPackageQuantity,
  getPackageEffectiveQuantity,
  getPackageTotalPrice,
  getPackagesTotalPrice,
  getDisplayedUnitPrice,
  getDisplayedPackageTotalPrice,
  getDisplayedPackagesTotalPrice,
  builderHasEditedPrices,
  withDisplayedUnitPrices,
  shouldOmitDefaultPackagingSave,
  hierarchyHasDefaultPricing,
  getPackageTotalCost,
  getPackagesTotalCost,
  getPackageAssignedItemCount,
  getPackageAssignedItemQuantity,
  getPackageDescendantIds,
  getPackageParentLabel,
  isDocumentItemPackage,
  isPackagingDocumentItemCode,
  documentHasPackagingItem,
  shouldShowDocumentItemInPackagingItems,
  shouldWarnPackagingOnItemDelete,
  mapPackagingMaterialTypeOptions,
  mapPackagingHierarchyNodes,
  mapDocumentItemsToPackagingItems,
  mapPackagingHierarchyResponse,
  normalizePackagingHierarchyBuilderState,
  preserveManuallyEditedUnitPrices,
  resolvePackagingSourceContext,
};
